// src/components/common/InvoiceRefundDialog.js — refunddialog-4e9b7c25 (R1d-2)
// "Why are you refunding?" — the invoice refund dialog for companies with the new refund flow
// (organizations/{org}.invoiceRefundFlowEnabled, set only by Marcus). Companies without it, and
// deposit refunds on estimates, keep RefundModal.js untouched.
//
// Steps: why → amount, method and bank account → summary (with the credit-note email box) →
// confirm → result. All the rules live in utils/invoiceRefundDialog.js; this file is the screen.
// The server decides every refund (refundInvoicePayment, Owner / Admin / Manager) and its refusals
// are shown word for word.
import React, { useEffect, useRef, useState } from 'react';
import { X, RotateCcw, AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase/config';
import { formatCurrency } from '../../utils/formatters';
import {
    REASONS, CARD_BANK_HINT, RETURN_INSTEAD_LABEL, INVOICE_CHANGED, METHOD_OPTIONS,
    newRequestId, sameLine, refundLimits, maxFor, reduceSaleBlock, bankPickerApplies, parseAmount, amountInvalid,
    buildRefundPayload, readRefundReply, readRefundError, summaryRows,
} from '../../utils/invoiceRefundDialog';

const InvoiceRefundDialog = ({ isOpen, onClose, onDone, orgId, invoice, clicked, companySettings, currencyCode = 'USD' }) => {
    const [step, setStep] = useState('loading'); // loading | changed | reason | details | summary | result
    const [loadError, setLoadError] = useState('');
    const [fresh, setFresh] = useState(null);
    const [reason, setReason] = useState('');
    const [card, setCard] = useState(false);
    const [method, setMethod] = useState(METHOD_OPTIONS[0].value);
    const [amountText, setAmountText] = useState('');
    const [banks, setBanks] = useState({ status: 'idle', list: [], error: '' }); // idle | loading | ready | error
    const [bankId, setBankId] = useState('');
    const [emailCreditNote, setEmailCreditNote] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    // ONE request id per open: a double click or a retry re-sends the same id, so it can't refund twice.
    const requestIdRef = useRef(null);
    const sendingRef = useRef(false);

    const invoiceId = invoice?.id;
    const sourceIndex = clicked?.idx;

    useEffect(() => {
        if (!isOpen || !invoiceId) return undefined;
        let live = true;
        requestIdRef.current = newRequestId();
        sendingRef.current = false;
        setStep('loading'); setLoadError(''); setFresh(null); setReason(''); setCard(false); setMethod(METHOD_OPTIONS[0].value);
        setAmountText(''); setBanks({ status: 'idle', list: [], error: '' }); setBankId(''); setEmailCreditNote(true);
        setSubmitting(false); setError(null); setResult(null);
        (async () => {
            try {
                // The page's copy of the invoice can be stale, and the server picks the payment by its
                // position — so read the invoice again and check the clicked line is still that line.
                const snap = await getDoc(doc(db, `organizations/${orgId}/invoices/${invoiceId}`));
                if (!live) return;
                const data = snap.exists() ? { ...snap.data(), id: invoiceId } : null;
                const history = data && Array.isArray(data.paymentHistory) ? data.paymentHistory : [];
                if (!data || !sameLine(clicked?.payment, history[sourceIndex]) || !refundLimits(data, sourceIndex).ok) { setStep('changed'); return; }
                setFresh(data);
                setStep('reason');
                if (companySettings?.quickbooksConnected === true && data.qbInvoiceId) {
                    setBanks({ status: 'loading', list: [], error: '' });
                    try {
                        const res = await httpsCallable(functions, 'getQuickBooksBankAccounts')({ bankOnly: true });
                        if (!live) return;
                        const list = Array.isArray(res?.data?.accounts) ? res.data.accounts : [];
                        setBanks({ status: 'ready', list, error: '' });
                        const preset = companySettings?.quickbooksSettings?.refundFromAccountId;
                        if (preset && list.some((a) => String(a.id) === String(preset))) setBankId(String(preset));
                    } catch (e) {
                        if (live) setBanks({ status: 'error', list: [], error: e?.message || 'QuickBooks could not be reached.' });
                    }
                }
            } catch (e) {
                if (live) { setLoadError(e?.message || 'The invoice could not be loaded.'); setStep('changed'); }
            }
        })();
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, invoiceId, sourceIndex]);

    if (!isOpen) return null;

    const money = (n) => formatCurrency(n, currencyCode);
    const limits = fresh ? refundLimits(fresh, sourceIndex) : { ok: false };
    const reduceBlock = fresh ? reduceSaleBlock(fresh, invoiceId, companySettings) : null;
    const reduceOff = !!reduceBlock || !(limits.ok && limits.reduceMax > 0);
    const max = maxFor(limits, reason);
    const needsBank = limits.ok && bankPickerApplies({ companySettings, invoice: fresh, row: limits.row, reason });
    const bankChoiceOwed = needsBank && banks.status === 'ready' && banks.list.length > 0 && !bankId;
    const bankName = needsBank && bankId ? (banks.list.find((a) => String(a.id) === String(bankId))?.name || '') : '';
    const emailTo = fresh?.customerDetails?.email || '';
    const detailsBlocked = amountInvalid(amountText, max) || (needsBank && banks.status === 'loading') || bankChoiceOwed;

    const pickReason = (r) => { setReason(r); setAmountText(maxFor(limits, r).toFixed(2)); setCard(limits.card); setError(null); };
    const useReturnInstead = () => {
        const keep = Math.min(parseAmount(amountText) || limits.returnMax, limits.returnMax);
        setReason(REASONS.RETURN_PAYMENT); setAmountText(keep.toFixed(2)); setError(null); setStep('details');
    };

    const confirm = async () => {
        if (sendingRef.current) return; // a double click sends nothing twice
        sendingRef.current = true;
        setSubmitting(true); setError(null);
        try {
            const res = await httpsCallable(functions, 'refundInvoicePayment')(buildRefundPayload({
                invoiceId, reason, card, method, amount: parseAmount(amountText), sourceIndex,
                requestId: requestIdRef.current, emailCreditNote, bankAccountId: needsBank ? bankId : '',
            }));
            const reply = readRefundReply(res?.data);
            if (reply.kind === 'failed') requestIdRef.current = newRequestId(); // that id is spent
            setResult(reply);
            setStep('result');
        } catch (e) {
            const refusal = readRefundError(e);
            if (refusal.needsNewId) requestIdRef.current = newRequestId();
            setError(refusal);
        } finally {
            sendingRef.current = false;
            setSubmitting(false);
        }
    };

    const close = step === 'result' ? onDone : onClose;
    const choice = (active, disabled) => `flex items-start gap-3 rounded-md border p-3 ${disabled ? 'border-gray-200 bg-gray-50 opacity-70 cursor-not-allowed' : active ? 'border-[#5F4EC9] bg-purple-50 cursor-pointer' : 'border-gray-300 hover:bg-gray-50 cursor-pointer'}`;
    const primaryBtn = 'flex-1 px-4 py-2 bg-orange-600 text-white rounded-md font-bold hover:bg-orange-700 disabled:opacity-50 flex items-center justify-center gap-2';
    const plainBtn = 'flex-1 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 disabled:opacity-50';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] overflow-y-auto" role="dialog" aria-label="Refund a payment">
                <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
                    <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                        <RotateCcw size={20} className="text-orange-600" /> Refund a payment
                    </h2>
                    <button onClick={close} disabled={submitting} aria-label="Close dialog" className="text-gray-500 hover:text-gray-700 disabled:opacity-50">
                        <X size={24} />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    {step === 'loading' && (
                        <p className="text-sm text-gray-600 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Checking the invoice…</p>
                    )}

                    {step === 'changed' && (
                        <>
                            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                                <span>{loadError || INVOICE_CHANGED}</span>
                            </div>
                            <button onClick={onClose} className={plainBtn}>Close</button>
                        </>
                    )}

                    {step === 'reason' && (
                        <>
                            <p className="text-sm text-gray-600">
                                Invoice #{fresh.invoiceNumber || ''} — the {money(limits.row.amount)} {limits.row.method || 'payment'}. Up to {money(limits.returnMax)} can be refunded from it.
                            </p>
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">Why are you refunding?</label>
                                <label className={choice(reason === REASONS.REDUCE_SALE, reduceOff)}>
                                    <input type="radio" name="refund-reason" className="mt-1" disabled={reduceOff}
                                        checked={reason === REASONS.REDUCE_SALE} onChange={() => pickReason(REASONS.REDUCE_SALE)} />
                                    <span className="text-sm">
                                        <span className="font-medium text-gray-900">Reduce the sale</span>
                                        <span className="block text-xs text-gray-500">The customer was charged too much. A credit note is issued, the tax is reversed and the invoice stays paid.</span>
                                        {reduceBlock && <span className="block text-xs text-amber-700 mt-1">{reduceBlock}</span>}
                                        {!reduceBlock && reduceOff && <span className="block text-xs text-amber-700 mt-1">Earlier credit notes already cover all of this sale.</span>}
                                    </span>
                                </label>
                                <label className={choice(reason === REASONS.RETURN_PAYMENT, false)}>
                                    <input type="radio" name="refund-reason" className="mt-1"
                                        checked={reason === REASONS.RETURN_PAYMENT} onChange={() => pickReason(REASONS.RETURN_PAYMENT)} />
                                    <span className="text-sm">
                                        <span className="font-medium text-gray-900">Return a payment</span>
                                        <span className="block text-xs text-gray-500">The money goes back but the sale stands. The invoice reopens and shows the amount as owing again.</span>
                                    </span>
                                </label>
                            </div>
                            <div className="flex gap-2 pt-2">
                                <button onClick={onClose} className={plainBtn}>Cancel</button>
                                <button onClick={() => setStep('details')} disabled={!reason} className={primaryBtn}>Next</button>
                            </div>
                        </>
                    )}

                    {step === 'details' && (
                        <>
                            <div>
                                <label htmlFor="refund-amount" className="block text-sm font-medium text-gray-700 mb-1">Refund amount</label>
                                <input id="refund-amount" type="number" min="0" step="0.01" value={amountText} onChange={(e) => setAmountText(e.target.value)}
                                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-[#5F4EC9] focus:border-[#5F4EC9]" />
                                <p className="text-xs text-gray-500 mt-1">Up to {money(max)} can be refunded.</p>
                                {amountInvalid(amountText, max) && amountText !== '' && (
                                    <p className="text-xs text-red-500 mt-1">Enter an amount greater than zero and no more than {money(max)}.</p>
                                )}
                            </div>

                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">How is the refund being made?</label>
                                {limits.card && (
                                    <label className={choice(card, false)}>
                                        <input type="radio" name="refund-how" className="mt-1" checked={card} onChange={() => setCard(true)} />
                                        <span className="text-sm">
                                            <span className="font-medium text-gray-900">Refund to card</span>
                                            <span className="block text-xs text-gray-500">The refund goes back to the customer's card — it can take a few business days to appear.</span>
                                        </span>
                                    </label>
                                )}
                                {limits.card && (
                                    <label className={choice(!card, false)}>
                                        <input type="radio" name="refund-how" className="mt-1" checked={!card} onChange={() => setCard(false)} />
                                        <span className="text-sm">
                                            <span className="font-medium text-gray-900">Refund taken another way</span>
                                            <span className="block text-xs text-gray-500">Cheque, cash, or e-transfer handed back to the customer.</span>
                                        </span>
                                    </label>
                                )}
                                {!card && (
                                    <select aria-label="Refund method" value={method} onChange={(e) => setMethod(e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5F4EC9]">
                                        {METHOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                )}
                            </div>

                            {needsBank && (
                                <div>
                                    <label htmlFor="refund-bank" className="block text-sm font-medium text-gray-700 mb-1">Bank account the refund comes from (QuickBooks)</label>
                                    {banks.status === 'loading' && (
                                        <p className="text-sm text-gray-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading your bank accounts…</p>
                                    )}
                                    {banks.status === 'ready' && banks.list.length > 0 && (
                                        <select id="refund-bank" value={bankId} onChange={(e) => setBankId(e.target.value)}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5F4EC9]">
                                            <option value="">-- Choose a bank account --</option>
                                            {banks.list.map((a) => <option key={a.id} value={String(a.id)}>{a.name}</option>)}
                                        </select>
                                    )}
                                    {(banks.status === 'error' || (banks.status === 'ready' && banks.list.length === 0)) && (
                                        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                                            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                                            <span>
                                                {banks.status === 'error' ? `Your bank accounts couldn't be loaded from QuickBooks (${banks.error}).` : 'QuickBooks returned no bank accounts.'}
                                                {' '}You can still refund: QuickBooks will use the Refund-from Bank Account in Settings → Integrations, or wait until one is chosen.
                                            </span>
                                        </div>
                                    )}
                                    {card && <p className="text-xs text-gray-500 mt-1">{CARD_BANK_HINT}</p>}
                                </div>
                            )}

                            <div className="flex gap-2 pt-2">
                                <button onClick={() => setStep('reason')} className={plainBtn}>Back</button>
                                <button onClick={() => setStep('summary')} disabled={detailsBlocked} className={primaryBtn}>Next</button>
                            </div>
                        </>
                    )}

                    {step === 'summary' && (
                        <>
                            <p className="text-sm font-medium text-gray-700">Check before you confirm</p>
                            <dl className="text-sm border rounded-lg divide-y">
                                {summaryRows({ reason, amountText, card, method, bankName, creditNoteEmail: emailCreditNote, emailTo, formatMoney: money }).map((r) => (
                                    <div key={r.label} className="flex justify-between gap-4 px-3 py-2">
                                        <dt className="text-gray-500 shrink-0">{r.label}</dt>
                                        {/* sumbox-5c1f8e36 -- the email row IS the checkbox: one control, so the summary can't say one thing and the box another. */}
                                        {r.isEmailChoice ? (
                                            <dd className="text-gray-900 text-right">
                                                <label className="inline-flex items-center gap-2 cursor-pointer">
                                                    <input type="checkbox" aria-label="Email the credit note" checked={emailCreditNote} onChange={(e) => setEmailCreditNote(e.target.checked)} />
                                                    <span>{r.value}</span>
                                                </label>
                                            </dd>
                                        ) : (
                                            <dd className="text-gray-900 text-right">{r.value}</dd>
                                        )}
                                    </div>
                                ))}
                            </dl>
                            {card && (
                                <p className="text-xs text-gray-500">The customer is emailed about a card refund either way.</p>
                            )}
                            {error && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm space-y-2" role="alert">
                                    <div className="flex items-start gap-2">
                                        <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                                        <span>{error.message}</span>
                                    </div>
                                    {error.offerReturnPayment && reason === REASONS.REDUCE_SALE && (
                                        <button onClick={useReturnInstead} className="text-sm font-medium text-[#5F4EC9] hover:underline">{RETURN_INSTEAD_LABEL}</button>
                                    )}
                                </div>
                            )}
                            <div className="flex gap-2 pt-2">
                                <button onClick={() => { setError(null); setStep('details'); }} disabled={submitting} className={plainBtn}>Back</button>
                                <button onClick={confirm} disabled={submitting} className={primaryBtn}>
                                    {submitting ? <Loader2 size={16} className="animate-spin" /> : 'Issue refund'}
                                </button>
                            </div>
                        </>
                    )}

                    {step === 'result' && result && (
                        <>
                            {result.kind === 'failed' ? (
                                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm" role="alert">
                                    <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                                    <span>{result.message}</span>
                                </div>
                            ) : (
                                <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
                                    <CheckCircle size={16} className="mt-0.5 shrink-0" />
                                    <span>
                                        {result.kind === 'pending'
                                            ? `The ${money(parseAmount(amountText))} card refund has been sent to Stripe. The invoice updates when Stripe confirms it, usually within a few minutes.`
                                            : `${result.already ? 'This refund was already recorded' : 'Refund recorded'}: ${money(parseAmount(amountText))}.`}
                                        {result.kind === 'done' && result.creditNoteNumber && ` Credit note ${result.creditNoteNumber} was issued${emailCreditNote ? ' and is being emailed to the customer' : ''}.`}
                                        {result.kind === 'done' && reason === REASONS.RETURN_PAYMENT && ' The invoice has reopened.'}
                                    </span>
                                </div>
                            )}
                            <div className="flex gap-2 pt-2">
                                {result.kind === 'failed' && (
                                    <button onClick={() => { setResult(null); setStep('summary'); }} className={plainBtn}>Try again</button>
                                )}
                                <button onClick={onDone} className={primaryBtn}>Close</button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InvoiceRefundDialog;
